import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { StatusBadge, Money, EmergencyBanner } from "@/components/shared/Common";
import { Star, Languages, GraduationCap, MapPin, Calendar, ArrowRight } from "lucide-react";

const DAYS = ["mon","tue","wed","thu","fri","sat","sun"];
const DAY_LABELS = { mon:"Mon", tue:"Tue", wed:"Wed", thu:"Thu", fri:"Fri", sat:"Sat", sun:"Sun" };

export default function DoctorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/doctors/${id}`).then(r => setData(r.data));
  }, [id]);

  if (!data) return <div className="cm-container py-16 text-center text-[#475569]">Loading…</div>;
  const d = data.doctor;
  const avail = data.availability;

  const book = () => {
    if (!user) return nav("/login?next=/patient/book/" + id);
    if (user.role !== "patient") return nav("/patient/book/" + id);
    nav(`/patient/book/${id}`);
  };

  return (
    <div className="cm-container py-12">
      <div className="mb-6"><EmergencyBanner compact /></div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="cm-card">
            <div className="flex items-start gap-5">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#f5f7fa] flex-shrink-0">
                {d.photo ? <img src={d.photo} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[#4a90d9] text-3xl font-bold">{d.first_name[0]}</div>}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h1 className="text-2xl font-bold text-[#1a2b4a]" data-testid="doctor-name">Dr. {d.first_name} {d.last_name}</h1>
                    <div className="text-sm text-[#4a90d9] capitalize font-medium">{(d.specialty||"").replace("_"," ")}</div>
                  </div>
                  <StatusBadge status={d.verification_status}/>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-[#475569]">
                  {d.rating > 0 && <span className="flex items-center gap-1"><Star size={14} className="text-[#d97706] fill-current"/>{Number(d.rating).toFixed(1)} · {d.reviews_count}</span>}
                  {d.years_experience > 0 && <span>{d.years_experience} years</span>}
                  {d.languages?.length > 0 && <span className="flex items-center gap-1"><Languages size={14}/> {d.languages.join(", ")}</span>}
                </div>
              </div>
            </div>
            <p className="mt-6 text-[#475569] leading-relaxed">{d.bio}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-[#475569]"><GraduationCap size={16} className="text-[#4a90d9] mt-0.5"/><div><div className="font-medium text-[#1a2b4a]">Qualifications</div>{d.qualifications || "—"}</div></div>
              <div className="flex items-start gap-2 text-[#475569]"><MapPin size={16} className="text-[#4a90d9] mt-0.5"/><div><div className="font-medium text-[#1a2b4a]">Registration</div>{d.registration_number || "—"}</div></div>
            </div>
          </div>

          {avail && (
            <div className="cm-card mt-6">
              <div className="flex items-center gap-2 mb-4"><Calendar size={16} className="text-[#4a90d9]"/><h3 className="font-semibold text-[#1a2b4a]">Weekly availability</h3></div>
              <div className="grid grid-cols-7 gap-2">
                {DAYS.map(day => (
                  <div key={day} className="rounded-lg border border-[#e2e8f0] p-2">
                    <div className="text-xs font-semibold text-[#1a2b4a] mb-1.5">{DAY_LABELS[day]}</div>
                    <div className="space-y-1">
                      {(avail.weekly_slots?.[day] || []).slice(0,4).map(t => (
                        <div key={t} className="text-xs bg-[#ebf3fc] text-[#1a2b4a] rounded px-1.5 py-0.5 text-center">{t}</div>
                      ))}
                      {(!avail.weekly_slots?.[day] || avail.weekly_slots[day].length === 0) && <div className="text-xs text-[#94a3b8] text-center">—</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="cm-card lg:sticky lg:top-24">
            <div className="cm-eyebrow mb-2">Book a consultation</div>
            <div className="space-y-2 mb-4">
              {(d.consultation_types||[]).includes("general") && (
                <div className="rounded-lg border border-[#e2e8f0] p-3">
                  <div className="text-sm font-semibold text-[#1a2b4a]">General consultation</div>
                  <div className="text-xs text-[#475569] mt-0.5">15 min · GP</div>
                </div>
              )}
              {(d.consultation_types||[]).includes("specialist") && (
                <div className="rounded-lg border border-[#e2e8f0] p-3">
                  <div className="text-sm font-semibold text-[#1a2b4a]">Specialist consultation</div>
                  <div className="text-xs text-[#475569] mt-0.5">20 min · Specialist</div>
                </div>
              )}
            </div>
            <button onClick={book} className="cm-btn-primary w-full" data-testid="doctor-profile-book-btn">Book with Dr. {d.last_name} <ArrowRight size={16}/></button>
            <p className="text-xs text-[#94a3b8] text-center mt-3">Fair pricing shown at checkout · cancel up to 24h before for a full refund.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
